import DeleteOutline from "@mui/icons-material/DeleteOutline";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { type FC, type ReactElement, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useApiServiceCallback } from "../../../server/api/client";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { toISO8601 } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";
import { useAnalytics } from "../../analytics/analytics";
import { ConditionalWrapper } from "../../generic/components/conditional-wrapper";
import { MomentService } from "../../moment/client";
import { SupabaseMomentService } from "../../moment/server/supabase-moment-service";

interface CardArchiveButtonProps {
  id: UUID;
  isDeleted: boolean;
  isArchived: boolean;
  isActive: boolean;
  unsavedChanges: boolean;
  status: "draft" | "paused" | "finished" | "active" | "archived" | null;
  onClicked?: (archived: boolean) => void;
}

export const CardArchiveButton = ({
  id,
  isActive,
  isArchived,
  isDeleted,
  unsavedChanges,
  onClicked,
  status,
}: CardArchiveButtonProps): ReactElement => {
  const dialog = useDialog();

  const archiveTooltip = isActive
    ? "Archive is unavailable for active Communications. Please pause the Communication first."
    : unsavedChanges
      ? "Save your changes to archive this Communication."
      : "Archive Communication";

  return (
    <>
      {isDeleted ? (
        <Tooltip title="Unarchive" placement="top" arrow>
          <IconButton data-analytics-id="moment-card-unarchive" onClick={dialog.handleOpen}>
            <RestoreFromTrash data-testid="RestoreFromTrashIcon" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip
          title={<Box sx={{ textAlign: "center" }}>{archiveTooltip}</Box>}
          placement="top"
          arrow
        >
          <ConditionalWrapper
            condition={isActive || unsavedChanges}
            wrapper={(children) => <span>{children}</span>}
          >
            <IconButton
              data-analytics-id="moment-card-archive"
              onClick={dialog.handleOpen}
              disabled={isActive || unsavedChanges}
            >
              <DeleteOutline data-testid="DeleteOutlineIcon" />
            </IconButton>
          </ConditionalWrapper>
        </Tooltip>
      )}
      <ArchiveDialog
        id={id}
        status={status}
        open={dialog.open}
        setOpen={dialog.setOpen}
        moment_archived={isArchived}
        onClicked={onClicked}
      />
    </>
  );
};

const ArchiveDialog: FC<{
  id: UUID;
  open: boolean;
  setOpen: (open: boolean) => void;
  moment_archived: boolean;
  onClicked?: (archived: boolean) => void;
  status: "draft" | "paused" | "finished" | "active" | "archived" | null;
}> = ({ open, setOpen, moment_archived, id, onClicked, status }) => {
  const [archiving, setArchiving] = useState(false);
  const { gaEvent } = useAnalytics();

  const pauseCallback = useApiServiceCallback(
    MomentService,
    async ({ apiService }): Promise<boolean> => {
      if (status !== "finished") {
        return true;
      }

      const response = await apiService.setActiveStatus(id, false, false);
      if (response.error && response.status !== 400) {
        toast.error(`Something went wrong: ${response.error}`);
        return false;
      }

      gaEvent("pause_moment", { moment_id: id, event_category: "moment" });
      return true;
    },
    [id, gaEvent],
  );

  const archiveCallback = useSupabaseCallback(
    async ({ supabase }, archive: boolean) => {
      setArchiving(true);

      const new_deleted_at = archive ? toISO8601(new Date()) : null;
      const { data, error } = await new SupabaseMomentService(supabase).update(id, {
        deleted_at: new_deleted_at,
      });

      if (!data || error) {
        toast.error(`Something went wrong: ${error}`);
        setArchiving(false);
        return;
      }

      toast.success(
        archive ? "Communication successfully archived" : "Communication successfully unarchived",
      );
      setArchiving(false);
      if (archive) {
        gaEvent("archive_moment", { moment_id: id, event_category: "moment" });
      } else {
        gaEvent("unarchive_moment", { moment_id: id, event_category: "moment" });
      }
      onClicked?.(archive);
    },
    [id, onClicked, gaEvent],
  );

  const combinedCallback = useCallback(
    async (value: boolean): Promise<void> => {
      const pauseResult = await pauseCallback();
      if (pauseResult) {
        await archiveCallback(value);
      }
    },
    [archiveCallback, pauseCallback],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogTitle id="alert-dialog-title">
        {moment_archived ? "Unarchive Communication" : "Archive Communication"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {moment_archived
            ? "Are you sure you want to unarchive this Communication?"
            : "Are you sure you want to archive this Communication?"}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id={`moment-card-${moment_archived ? "unarchive" : "archive"}-dialog-cancel`}
          onClick={() => {
            setOpen(false);
          }}
          disabled={archiving}
        >
          Cancel
        </Button>
        <Button
          data-analytics-id={`moment-card-${moment_archived ? "unarchive" : "archive"}-dialog-confirm`}
          loading={archiving}
          variant="contained"
          onClick={() => {
            setOpen(false);
            void combinedCallback(!moment_archived).catch(console.error);
          }}
        >
          Yes, I’m sure
        </Button>
      </DialogActions>
    </Dialog>
  );
};
